import { useState } from 'react';
import { useSelector } from 'react-redux';
import { AutoSizer, Table, Column, SortDirection } from 'react-virtualized';
import { Button, Col, Row, Spinner, Input, InputGroup, InputGroupText } from 'reactstrap';
import { Chart as ChartJS, registerables } from 'chart.js';
import Title from '../../../sharedComponents/Title/Title';
import useDataSuite from '../useDataSuite';
import { FaClipboardList, FaTrash } from 'react-icons/fa';
import { BsPencilSquare, BsSearch } from 'react-icons/bs';
import { IoMdInformationCircle } from "react-icons/io";
import { Tooltip } from "@mui/material";
import useDataSuiteWidgets from './useDataSuiteWidgets';
import EmptyDataMessage from '../../../sharedComponents/ErrorMessages/EmptyDataMessage';
import { Theme } from '../../../config/constants';
import formatters from '../../../config/formatters';
import { sortableHeaderRenderer } from '../../../sharedComponents/Tables/SortableHeaderRenderer';

ChartJS.register(...registerables);

const TargetsByCategoryWidget = (props) => {
    const { w, i } = props;
    const numberFormatter = formatters().numberFormatter;
    const { onClickWidgetDrawerOpen, onClickDeleteWidget } = useDataSuite.useWidgetControls();
    const { widgetTitle, targetsByCategoryTableData } = useDataSuiteWidgets.useTargetsByCategoryWidget();
    const { onClickViewDetails } = useDataSuite.useDataSuite();
    const widgetOptionsOpen = useSelector(({ui}) => ui.slideDrawer);
    const isLoading = useSelector(({datasuite}) => datasuite.isLoading.targetsByCategoryLoading);
    const [isWidgetHovered, setIsWidgetHovered] = useState(false);
    const [sortBy, setSortBy] = useState('');
    const [sortDirection, setSortDirection] = useState(null);
    const [searchText, setSearchText] = useState('');

    const handleMouseEnter = () => {
        setIsWidgetHovered(true);
    };
    const handleMouseLeave = () => {
        setIsWidgetHovered(false);
    };

    const sortedTargetsByCategoryTableData = (() => {
        if (!targetsByCategoryTableData) return [];
        const sorted = [...targetsByCategoryTableData];
        if (sortBy === 'total_cases_comp') {
            sorted.sort((a, b) => {
                if (sortDirection === SortDirection.ASC) {
                    return (a.total_cases_comp || 0) - (b.total_cases_comp || 0);
                } else {
                    return (b.total_cases_comp || 0) - (a.total_cases_comp || 0);
                }
            });
        }
        return sorted;
    })();

    return (
        <Col onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} style={{ position: 'relative'}} >
            <Row className='d-flex align-items-center'>
                <Col className='w-fc pr-0'>
                    <div className='d-flex align-items-center px-0'>
                    <Title title={widgetTitle}/>
                    <Tooltip title='Displays operator purchases by your relevant categories compared to purchases in those same categories to competitive MFRs. Helpful for identifying category areas of growth to focus on.' arrow>
                        <div className='pt-3 pb-2'>
                        <IoMdInformationCircle className="text-size-18 " style={{color: Theme().colors.accent}}/>
                        </div>
                    </Tooltip>
                    </div>
                </Col>
                {(widgetOptionsOpen || isWidgetHovered)  && ( <>
                    <Col className='w-fc px-1 pt-3 pb-2'>
                        <Button
                            block
                            className='py-1 button-label replaceWidgetButton'
                            size='sm'
                            color='blueFilter'
                            onClick={() => onClickWidgetDrawerOpen("Replace Widget", true, i)}>
                            <BsPencilSquare color='white' />
                        </Button>
                    </Col>
                    <Col className='w-fc px-1 pt-3 pb-2'>
                        <Button
                            block
                            className='py-1 button-label deleteWidgetButton'
                            size='sm'
                            color='redLight'
                            onClick={() => onClickDeleteWidget(w)}>
                            <FaTrash color='white' />
                        </Button>
                    </Col>
                </>
                )}
                <Col className={`w-fc me-3 ms-auto px-1 pt-3 pb-2`}>
                    <Button
                        block
                        className={`py-1 button-label expandWidgetButton`}
                        size='sm'
                        outline={true}
                        color={Theme().colors.accentName}
                        onClick={() => onClickViewDetails(widgetTitle, props.prechosenFilters)}>
                        <FaClipboardList color={Theme().colors.accentName} style={{position: 'relative', bottom: '1px'}}/> View Details
                    </Button>
                </Col>
            </Row>
            <Row className='mb-2'>
                <InputGroup size='sm'>
                    <InputGroupText>
                        <BsSearch/>
                    </InputGroupText>
                    <Input
                        className='text-input'
                        id="targetsByCategorySearchInput"
                        placeholder='Search...'
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                    />
                </InputGroup>
            </Row>
            {isLoading &&
              <Row className="mx-auto" style={{minHeight: '315px'}}>
                  <Col className="mx-auto my-auto" style={{width:"2.5%"}}>
                      <Spinner style={{marginLeft:'50%'}} color='beplBlue' animation="border" />
                  </Col>
              </Row>
            }
            {(sortedTargetsByCategoryTableData?.length <= 0) && !isLoading &&
            <Row className="mx-auto" style={{minHeight: '315px'}}>
                <Col className="mx-auto my-auto" style={{width:"2.5%"}}>
                    {!isLoading && (sortedTargetsByCategoryTableData.length <= 0) &&
                    <EmptyDataMessage />
                    }
                </Col>
            </Row>
            }
            {(sortedTargetsByCategoryTableData?.length > 0) && !isLoading &&
            <Row className="w-100 mb-3 mx-0" style={{height: '350px'}}>
                <Col className="col-12 text-size-14 p-0 h-100">
                        <Row style={{overflowY: 'auto', height: '100%'}}>
                            <AutoSizer>
                                {({width, height}) => (
                                    <Table className="table leakageTable border-grayLight" id="leakageTable"
                                        width={width * 0.97}
                                        height={height}
                                        headerHeight={34.5}
                                        rowHeight={38}
                                        rowCount={
                                            searchText !== ''
                                                ? sortedTargetsByCategoryTableData.filter(row =>
                                                    row.category.toLowerCase().includes(searchText.toLowerCase())
                                                ).length
                                                : sortedTargetsByCategoryTableData.length
                                        }
                                        rowGetter={({ index }) =>
                                            searchText !== ''
                                                ? sortedTargetsByCategoryTableData.filter(row =>
                                                    row.category.toLowerCase().includes(searchText.toLowerCase())
                                                )[index]
                                                : sortedTargetsByCategoryTableData[index]
                                        }
                                        sort={({ sortBy: sb, sortDirection: sd }) => {
                                            setSortBy(sb);
                                            setSortDirection(sd);
                                        }}
                                        sortBy={sortBy}
                                        sortDirection={sortDirection}
                                    >
                                        <Column
                                        label='Category'
                                        dataKey='category'
                                        width={width * 0.4}
                                        disableSort
                                        />
                                        <Column
                                        width={width * 0.3}
                                        label='Your Cases'
                                        dataKey='total_cases'
                                        cellRenderer={({cellData}) => numberFormatter.format(cellData)}
                                        disableSort
                                        />
                                        <Column
                                        width={width * 0.2}
                                        label='Opportunity Cases'
                                        dataKey='total_cases_comp'
                                        cellRenderer={({cellData}) => numberFormatter.format(cellData)}
                                        headerRenderer={sortableHeaderRenderer}
                                        />
                                    </Table>
                                )}
                            </AutoSizer>
                        </Row>
                </Col>
            </Row>
            }
        </Col>
    );

}

export default TargetsByCategoryWidget;
